import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaTimes, FaMicrophone, FaStop, FaPaperPlane } from 'react-icons/fa';
import * as aiService from '../../../influencer/services/aiService';
import './AIAssistant.css';

const AIAssistant = ({ context = 'general' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const getInitialMessage = () => {
    switch (context) {
      case 'homepage':
        return "Hi! Welcome to Infuber! 👋\n\nI'm your AI assistant here to help you learn about our platform. I can answer:\n• Why choose Infuber?\n• How to get started?\n• What services we offer\n• How the platform works\n\nWhat would you like to know?";
      case 'automation':
        return "Hi! I'm your AI assistant for automation. I can help you:\n• Analyze comment sentiment\n• Suggest keywords\n• Generate personalized messages\n• Optimize your automation settings\n\nHow can I help you today?";
      case 'dashboard':
        return "Hi! I'm your AI assistant. I can help you with:\n• Analyzing your performance metrics\n• Suggesting improvements\n• Explaining your statistics\n• Predicting trends\n\nHow can I help you today?";
      default:
        return "Hi! I'm your AI assistant. I can help you with:\n• Analyzing your content\n• Suggesting improvements\n• Answering questions about your dashboard\n• Generating message ideas\n\nHow can I help you today?";
    }
  };

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: getInitialMessage(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // Quick action buttons for homepage context
  const quickActions = context === 'homepage' ? [
    { id: 'why', label: 'Why Choose Infuber?', query: 'why choose infuber' },
    { id: 'start', label: 'How to Get Started', query: 'how to get started' },
    { id: 'services', label: 'Our Services', query: 'what services do you offer' },
    { id: 'works', label: 'How It Works', query: 'how does the platform work' },
    { id: 'earn', label: 'How to Earn', query: 'how to earn money' },
    { id: 'requirements', label: 'Requirements', query: 'what are the requirements' },
  ] : [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Context-specific help messages
  const getContextHelp = () => {
    switch (context) {
      case 'automation':
        return "I can help you:\n• Analyze comment sentiment\n• Suggest keywords\n• Generate personalized messages\n• Optimize your automation settings";
      case 'dashboard':
        return "I can help you:\n• Analyze your performance metrics\n• Suggest improvements\n• Explain your statistics\n• Predict trends";
      case 'campaign':
        return "I can help you:\n• Optimize campaign descriptions\n• Suggest commission rates\n• Match with influencers\n• Analyze campaign performance";
      case 'homepage':
        return "Hi! I'm here to help you learn about Infuber! I can answer:\n• Why choose Infuber?\n• How to get started?\n• What services we offer\n• How the platform works\n\nWhat would you like to know?";
      default:
        return "I can help you with various tasks. What would you like to know?";
    }
  };

  const handleSendMessage = async (messageText = null) => {
    const userMessage = messageText || input.trim();
    if (!userMessage || isLoading) return;

    setInput('');
    setShowQuickActions(false); // Hide quick actions after first interaction
    setIsLoading(true);

    // Add user message
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);

    try {
      // Simple AI response based on keywords and context
      let response = await generateAIResponse(userMessage, context);
      
      setMessages((prev) => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('AI response error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "I'm sorry, I encountered an error. Please try again or rephrase your question.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (query) => {
    handleSendMessage(query);
  };

  const generateAIResponse = async (userMessage, context) => {
    const lowerMessage = userMessage.toLowerCase();

    // Analyze sentiment
    const sentiment = await aiService.analyzeSentiment(userMessage);

    // Context-specific responses
    if (context === 'homepage') {
      if (lowerMessage.includes('why') || lowerMessage.includes('choose') || lowerMessage.includes('benefit')) {
        return "Why Choose Infuber? 🌟\n\n• **Multiple Earning Opportunities**: Earn through advertising, affiliate marketing, and building your own brand\n• **Real-time Analytics**: Track your performance with comprehensive insights and reporting\n• **Secure Platform**: Enterprise-grade security with advanced authentication\n• **AI-Powered Tools**: Smart automation and AI assistance to boost productivity\n• **Community Focus**: Connect with brands and fellow influencers\n• **Easy to Start**: Simple onboarding process - just connect your Instagram account\n• **Flexible Payment**: Multiple payment options and transparent earnings tracking\n\nReady to get started?";
      }
      if (lowerMessage.includes('start') || lowerMessage.includes('begin') || lowerMessage.includes('how to') || lowerMessage.includes('get started')) {
        return "How to Get Started with Infuber? 🚀\n\nStep 1: Click 'Login' or 'Sign Up' button\nStep 2: Connect your Instagram account (Business/Creator account recommended)\nStep 3: Complete your profile with details about yourself\nStep 4: Explore available campaigns or create affiliate links\nStep 5: Start earning by sharing content and links!\n\n**For Brands**:\n• Sign up as a company\n• Create campaigns\n• Invite influencers\n• Track performance\n\n**For Influencers**:\n• Connect social media\n• Browse campaigns\n• Generate affiliate links\n• Start earning!\n\nWould you like more details about any step?";
      }
      if (lowerMessage.includes('service') || lowerMessage.includes('offer') || lowerMessage.includes('what do you')) {
        return "Our Services at Infuber 💼\n\n**For Influencers**:\n• **Advertising**: Share brand reels and earn fixed + variable payouts\n• **Affiliate Marketing**: Earn commissions on sales through your unique links\n• **Build Your Brand**: Launch your own products with our guidance\n• **Video Production**: Professional content creation services\n\n**For Brands**:\n• **Campaign Management**: Create and manage influencer campaigns\n• **Influencer Matching**: Find the perfect creators for your brand\n• **Analytics & Reporting**: Track campaign performance in real-time\n• **Payment Processing**: Automated payment system for influencers\n\nWhich service interests you most?";
      }
      if (lowerMessage.includes('work') || lowerMessage.includes('platform') || lowerMessage.includes('how does')) {
        return "How Infuber Works 🔄\n\n**For Influencers**:\n1. Sign up and connect your Instagram account\n2. Browse available campaigns or create affiliate links\n3. Share content with your audience\n4. Track clicks, conversions, and earnings\n5. Get paid automatically\n\n**For Brands**:\n1. Create a company account\n2. Set up campaigns with budgets and goals\n3. Invite or discover influencers\n4. Monitor campaign performance\n5. Pay influencers based on results\n\n**Key Features**:\n• AI-powered automation for Instagram\n• Real-time analytics dashboard\n• Secure payment processing\n• Campaign management tools\n• Influencer marketplace\n\nWant to know more about any specific feature?";
      }
      if (lowerMessage.includes('earn') || lowerMessage.includes('money') || lowerMessage.includes('income')) {
        return "How to Earn with Infuber 💰\n\n**Multiple Income Streams**:\n\n1. **Advertising Campaigns**:\n   • Share brand reels/posts\n   • Earn fixed payments + engagement bonuses\n   • Get paid per view/engagement\n\n2. **Affiliate Marketing**:\n   • Generate unique affiliate links\n   • Share links with your audience\n   • Earn commissions on every sale\n   • Track conversions in real-time\n\n3. **Build Your Brand**:\n   • Launch your own products\n   • Use our platform to sell\n   • Keep 100% of your brand profits\n\n**Payment**:\n• Transparent earnings tracking\n• Monthly payment cycles\n• Multiple payment methods\n• Secure transactions\n\nReady to start earning?";
      }
      if (lowerMessage.includes('requirement') || lowerMessage.includes('need') || lowerMessage.includes('qualification')) {
        return "Requirements to Join Infuber 📋\n\n**For Influencers**:\n• Instagram account (Business or Creator account preferred)\n• Active social media presence\n• Willingness to create quality content\n• No minimum follower requirement!\n\n**For Brands**:\n• Business registration\n• Product/service to promote\n• Marketing budget\n• Clear campaign goals\n\n**Getting Started**:\n• Free to join\n• No upfront costs\n• Easy onboarding process\n• Support available\n\nJust sign up and connect your Instagram to begin!";
      }
    }

    if (context === 'automation') {
      if (lowerMessage.includes('keyword') || lowerMessage.includes('suggest')) {
        return "I can suggest keywords based on your product. Common effective keywords include: 'link, 'product', 'interested', 'want', 'buy', 'how much', 'price', 'love', 'amazing'. Would you like me to analyze your product description for specific keywords?";
      }
      if (lowerMessage.includes('message') || lowerMessage.includes('dm')) {
        return "For effective DMs, keep them:\n• Personal (use {{name}} or {{username}})\n• Friendly and conversational\n• Clear about the offer\n• Include a call-to-action\n\nWould you like me to generate a sample message for you?";
      }
      if (lowerMessage.includes('sentiment') || lowerMessage.includes('analyze')) {
        return `I analyzed your message sentiment: ${sentiment.label} (confidence: ${(sentiment.score * 100).toFixed(1)}%). This is good for ${sentiment.label === 'positive' ? 'engaging' : sentiment.label === 'negative' ? 'addressing concerns' : 'neutral communication'}.`;
      }
    }

    if (context === 'dashboard') {
      if (lowerMessage.includes('conversion') || lowerMessage.includes('rate')) {
        return "Conversion rate is the percentage of visitors who complete a desired action. To improve:\n• Share links in multiple places (bio, stories, posts)\n• Use compelling call-to-actions\n• Target the right audience\n• Test different messaging strategies";
      }
      if (lowerMessage.includes('earnings') || lowerMessage.includes('money')) {
        return "To increase earnings:\n• Focus on high-converting campaigns\n• Share links consistently\n• Engage with your audience\n• Diversify your campaigns\n• Optimize your content strategy";
      }
      if (lowerMessage.includes('link') || lowerMessage.includes('affiliate')) {
        return "Best practices for affiliate links:\n• Place in bio (most visible)\n• Share in stories (high engagement)\n• Include in post captions\n• Use link in highlights\n• Track which placements work best";
      }
    }

    // General responses
    if (lowerMessage.includes('help') || lowerMessage.includes('what can you')) {
      return getContextHelp();
    }

    if (lowerMessage.includes('hello') || lowerMessage.includes('hi')) {
      return "Hello! " + getContextHelp();
    }

    // Default: Generate a helpful response
    return `I understand you're asking about "${userMessage}". Based on the context, I'd suggest focusing on clear communication and consistent engagement. Would you like more specific advice?`;
  };

  const startListening = () => {
    if (!aiService.isSpeechRecognitionSupported()) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    try {
      const recognition = aiService.initSpeechRecognition(
        (transcript) => {
          setInput(transcript);
          setIsListening(false);
          recognitionRef.current = null;
        },
        (error) => {
          console.error('Speech recognition error:', error);
          setIsListening(false);
          recognitionRef.current = null;
        }
      );

      recognitionRef.current = recognition;
      recognition.start();
      setIsListening(true);
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      alert('Failed to start voice input. Please try typing instead.');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setIsListening(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      setShowQuickActions(false); // Hide quick actions when user types
    }
  };

  // Render message content with markdown support (bold, step numbers)
  const renderMessageContent = (content) => {
    if (!content) return '';
    
    // Split by lines to handle step formatting
    const lines = content.split('\n');
    
    return lines.map((line, lineIndex) => {
      // Check if line starts with Step X: (with or without **)
      const stepMatch = line.match(/^(\*\*)?(Step \d+)(\*\*)?:\s*(.*)/i);
      
      if (stepMatch) {
        const stepNumber = stepMatch[2]; // "Step 1"
        const stepText = stepMatch[4] || ''; // Rest of the text after colon
        return (
          <div key={lineIndex} style={{ marginBottom: lineIndex < lines.length - 1 ? '8px' : '0' }}>
            <strong style={{ fontWeight: '700', color: '#ff6b35' }}>{stepNumber}:</strong>
            {stepText && <span> {stepText}</span>}
          </div>
        );
      }
      
      // Handle bold text with **text**
      if (line.includes('**')) {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <div key={lineIndex} style={{ marginBottom: lineIndex < lines.length - 1 ? '8px' : '0' }}>
            {parts.map((part, partIndex) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                const boldText = part.slice(2, -2);
                return <strong key={partIndex} style={{ fontWeight: '700' }}>{boldText}</strong>;
              }
              return <span key={partIndex}>{part}</span>;
            })}
          </div>
        );
      }
      
      // Regular line
      return (
        <div key={lineIndex} style={{ marginBottom: lineIndex < lines.length - 1 ? '8px' : '0' }}>
          {line || '\u00A0'}
        </div>
      );
    });
  };

  if (!isOpen) {
    // return (
    //   <button
    //     onClick={() => setIsOpen(true)}
    //     className="ai-assistant-toggle"
    //     aria-label="Open AI Assistant"
    //   >
    //     <FaRobot className="text-2xl text-orange-500" />
    //   </button>
    // );
    return null;
  }

  return (
    <div className="ai-assistant-container">
      <div className="ai-assistant-header">
        <div className="flex items-center space-x-2">
          <FaRobot className="text-orange-500" />
          <h3>AI Assistant</h3>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="ai-assistant-close"
          aria-label="Close AI Assistant"
        >
          <FaTimes />
        </button>
      </div>

      <div className="ai-assistant-messages">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`ai-message ${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}
          >
            <div className="ai-message-content">
              {renderMessageContent(msg.content)}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="ai-message ai-message-assistant">
            <div className="ai-message-content">
              <div className="ai-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        {/* Quick Action Buttons - Show only for homepage context and when no messages sent yet */}
        {showQuickActions && quickActions.length > 0 && messages.length <= 1 && !isLoading && (
          <div className="ai-quick-actions">
            <p className="ai-quick-actions-title">Quick Questions:</p>
            <div className="ai-quick-actions-grid">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action.query)}
                  className="ai-quick-action-btn"
                  disabled={isLoading}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="ai-assistant-input">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (e.target.value.trim().length > 0) {
                setShowQuickActions(false); // Hide quick actions when user starts typing
              }
            }}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything..."
            className="ai-assistant-input-field"
            disabled={isLoading}
            autoComplete="off"
          />
          {aiService.isSpeechRecognitionSupported() && (
            <button
              onClick={isListening ? stopListening : startListening}
              className={`p-2 rounded-lg ${
                isListening
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              aria-label={isListening ? 'Stop listening' : 'Start voice input'}
            >
              {isListening ? <FaStop /> : <FaMicrophone />}
            </button>
          )}
          <button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading}
            className="p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <FaPaperPlane />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;

